import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, Eye, EyeOff, Upload, Youtube, FileVideo, ImagePlus } from 'lucide-react';
import { sanitizeFileName } from '@/lib/utils';
import { extractYouTubeId, youtubeThumbnail } from '@/lib/youtube';

interface VideoItem {
  id: string;
  title: string;
  description: string;
  source_type: 'upload' | 'youtube';
  video_url: string;
  poster_url: string | null;
  sort_order: number;
  visible: boolean;
}

const VideoGalleryAdmin = () => {
  const [items, setItems] = useState<VideoItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [ytUrl, setYtUrl] = useState('');
  const [ytTitle, setYtTitle] = useState('');
  const [savingYt, setSavingYt] = useState(false);
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const fetchItems = async () => {
    const { data } = await supabase
      .from('video_gallery_items')
      .select('*')
      .order('sort_order', { ascending: true });
    if (data) setItems(data as VideoItem[]);
  };

  useEffect(() => { fetchItems(); }, []);

  const nextSort = () => (items.length > 0 ? Math.max(...items.map(i => i.sort_order)) + 1 : 0);

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 52 * 1024 * 1024) {
      toast.error('Soubor je větší než 50 MB.');
      return;
    }
    setUploading(true);
    const safe = sanitizeFileName(file.name);
    const fileName = `video-${Date.now()}-${safe}`;
    const { error: uploadError } = await supabase.storage.from('gallery-videos').upload(fileName, file, {
      contentType: file.type,
    });
    if (uploadError) { toast.error('Chyba nahrávání: ' + uploadError.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('gallery-videos').getPublicUrl(fileName);
    const { error: insertError } = await supabase.from('video_gallery_items').insert({
      title: file.name.replace(/\.[^.]+$/, ''),
      source_type: 'upload',
      video_url: urlData.publicUrl,
      sort_order: nextSort(),
    });
    if (insertError) toast.error('Chyba při ukládání.');
    else toast.success('Video nahráno');
    fetchItems();
    setUploading(false);
  };

  const handleAddYouTube = async () => {
    const id = extractYouTubeId(ytUrl);
    if (!id) { toast.error('Neplatné YouTube URL'); return; }
    setSavingYt(true);
    const { error } = await supabase.from('video_gallery_items').insert({
      title: ytTitle.trim() || 'YouTube video',
      source_type: 'youtube',
      video_url: ytUrl.trim(),
      poster_url: youtubeThumbnail(id),
      sort_order: nextSort(),
    });
    if (error) toast.error('Chyba: ' + error.message);
    else { toast.success('YouTube video přidáno'); setYtUrl(''); setYtTitle(''); }
    setSavingYt(false);
    fetchItems();
  };

  const handlePosterUpload = async (item: VideoItem, file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('Vyberte obrázek'); return; }
    const safe = sanitizeFileName(file.name);
    const fileName = `poster-${Date.now()}-${safe}`;
    const { error: upErr } = await supabase.storage.from('gallery-videos').upload(fileName, file);
    if (upErr) { toast.error('Chyba nahrávání náhledu'); return; }
    const { data: urlData } = supabase.storage.from('gallery-videos').getPublicUrl(fileName);
    await updateItem(item.id, { poster_url: urlData.publicUrl });
    toast.success('Náhled aktualizován');
  };

  const updateItem = async (id: string, updates: Partial<VideoItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
    await supabase.from('video_gallery_items').update(updates).eq('id', id);
  };

  const deleteItem = async (item: VideoItem) => {
    if (!confirm('Opravdu smazat?')) return;
    // Try to delete uploaded file
    if (item.source_type === 'upload') {
      const fileName = item.video_url.split('/').pop();
      if (fileName) await supabase.storage.from('gallery-videos').remove([fileName]);
    }
    await supabase.from('video_gallery_items').delete().eq('id', item.id);
    toast.success('Smazáno');
    fetchItems();
  };

  const handleDragStart = (i: number) => { dragIndex.current = i; };
  const handleDragOver = (e: React.DragEvent, i: number) => { e.preventDefault(); setDragOver(i); };
  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOver(null);
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === dropIndex) return;
    const updated = [...items];
    const [moved] = updated.splice(from, 1);
    updated.splice(dropIndex, 0, moved);
    setItems(updated);
    await Promise.all(updated.map((it, i) =>
      supabase.from('video_gallery_items').update({ sort_order: i }).eq('id', it.id)
    ));
    toast.success('Pořadí uloženo');
  };

  return (
    <div>
      <h1 className="text-4xl tracking-wider text-foreground mb-2" style={{ fontFamily: 'Bebas Neue' }}>
        Video galerie ({items.length})
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Přidávej videoukázky masek — buď jako vlastní MP4 soubor (max 50 MB), nebo jako odkaz na YouTube.
      </p>

      {/* Add panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="p-5 bg-card border border-border rounded-md">
          <div className="flex items-center gap-2 mb-3">
            <FileVideo size={18} className="text-primary" />
            <h3 className="font-semibold text-foreground">Nahrát video soubor</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">MP4 nebo WebM, max 50 MB. Doporučeno 1080p, bez zvuku.</p>
          <label className="cursor-pointer block">
            <input type="file" accept="video/mp4,video/webm" onChange={handleVideoUpload} className="hidden" disabled={uploading} />
            <Button asChild disabled={uploading} className="w-full">
              <span className="inline-flex items-center justify-center gap-2">
                {uploading ? <Upload size={18} className="animate-pulse" /> : <Plus size={18} />}
                {uploading ? 'Nahrávám...' : 'Vybrat video'}
              </span>
            </Button>
          </label>
        </div>

        <div className="p-5 bg-card border border-border rounded-md">
          <div className="flex items-center gap-2 mb-3">
            <Youtube size={18} className="text-primary" />
            <h3 className="font-semibold text-foreground">Přidat z YouTube</h3>
          </div>
          <Input
            placeholder="https://youtube.com/watch?v=..."
            value={ytUrl}
            onChange={(e) => setYtUrl(e.target.value)}
            className="mb-2 bg-background border-border"
          />
          <Input
            placeholder="Název videa (nepovinné)"
            value={ytTitle}
            onChange={(e) => setYtTitle(e.target.value)}
            className="mb-2 bg-background border-border"
          />
          <Button onClick={handleAddYouTube} disabled={savingYt || !ytUrl.trim()} className="w-full">
            {savingYt ? 'Přidávám...' : 'Přidat YouTube video'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        💡 Tip: Tažením za <GripVertical size={12} className="inline" /> můžete měnit pořadí.
      </p>

      <div className="space-y-3">
        {items.map((item) => {
          const realIndex = items.indexOf(item);
          const poster = item.poster_url
            || (item.source_type === 'youtube' ? (() => {
                const id = extractYouTubeId(item.video_url);
                return id ? youtubeThumbnail(id) : '/placeholder.svg';
              })() : '/placeholder.svg');
          return (
            <div
              key={item.id}
              draggable
              onDragStart={() => handleDragStart(realIndex)}
              onDragOver={(e) => handleDragOver(e, realIndex)}
              onDrop={(e) => handleDrop(e, realIndex)}
              onDragEnd={() => setDragOver(null)}
              className={`flex items-center gap-4 p-4 bg-card border rounded-md transition-all cursor-move ${
                dragOver === realIndex ? 'border-primary scale-[1.01]' : 'border-border'
              }`}
            >
              <GripVertical size={20} className="text-muted-foreground shrink-0" />
              <div className="relative w-32 h-20 shrink-0">
                <img src={poster} alt={item.title} className="w-full h-full object-cover rounded" />
                <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] uppercase rounded bg-black/70 text-white">
                  {item.source_type === 'youtube' ? 'YT' : 'MP4'}
                </span>
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Input
                  value={item.title}
                  onChange={(e) => updateItem(item.id, { title: e.target.value })}
                  placeholder="Název"
                  className="bg-background border-border text-sm"
                />
                <Textarea
                  value={item.description || ''}
                  onChange={(e) => updateItem(item.id, { description: e.target.value })}
                  placeholder="Popis (nepovinné)"
                  rows={1}
                  className="bg-background border-border text-sm"
                />
                <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors">
                  <ImagePlus size={14} />
                  Změnit náhled
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePosterUpload(item, f); e.target.value = ''; }}
                  />
                </label>
              </div>
              <button
                onClick={() => updateItem(item.id, { visible: !item.visible })}
                className={`p-2 rounded transition-colors shrink-0 ${item.visible ? 'text-primary' : 'text-muted-foreground'}`}
                title={item.visible ? 'Skrýt' : 'Zobrazit'}
              >
                {item.visible ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              <button
                onClick={() => deleteItem(item)}
                className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                title="Smazat"
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-center text-muted-foreground py-12">Zatím žádná videa.</p>
        )}
      </div>
    </div>
  );
};

export default VideoGalleryAdmin;
